import { todayIso } from '@eo/shared';

import {
  CALENDAR_COMPACT_HEIGHT,
  CALENDAR_DAY_END_HOUR,
  CALENDAR_DAY_START_HOUR,
  CALENDAR_FULL_HEIGHT,
  CALENDAR_MIN_BLOCK_HEIGHT,
  CALENDAR_PX_PER_MINUTE,
  CALENDAR_SNAP_MINUTES,
  CALENDAR_TITLE_ONLY_COLUMNS,
  CALENDAR_SOURCE_NAMES,
  CALENDAR_WINDOW_PAD_MINUTES,
  TRANSCRIPT_RUN_GAP_MS,
} from './constants';

import type { CreateEntryBody, DismissalBody, TimelineRow, TranscriptLine } from './api/types';
import type {
  BlockLayout,
  DraftSelection,
  DrawnSpan,
  LogEntryPayload,
  RowOverride,
  TranscriptGroup,
  TranscriptRun,
} from './types';

export function parseHiddenSources(param: string | null): Set<TimelineRow['kind']> {
  const named = (param ?? '').split(',');
  return new Set(
    (Object.keys(CALENDAR_SOURCE_NAMES) as Array<TimelineRow['kind']>).filter((kind) =>
      named.includes(kind),
    ),
  );
}

export function toggleHiddenSource(
  hidden: ReadonlySet<TimelineRow['kind']>,
  kind: TimelineRow['kind'],
): string {
  const next = new Set(hidden);
  if (next.has(kind)) next.delete(kind);
  else next.add(kind);
  return [...next].join(',');
}

// A row already knows where it sits and how long it ran; confirming it is
// logging exactly that, with the origin recorded so a meeting stays confirmed
// after a reload.
// A row's kind names its source; a decision's kind names where the entry came
// from. They agree everywhere but Calendar, whose rows read 'meeting'.
const ORIGIN_KIND: Record<'task' | 'meeting' | 'slack', 'task' | 'calendar' | 'slack'> = {
  task: 'task',
  meeting: 'calendar',
  slack: 'slack',
};

// A dismissal is matched against the same source key the day was built from;
// filed under the wrong kind it matches nothing and the row comes back.
export function dismissalKind(row: TimelineRow): DismissalBody['kind'] {
  return row.kind === 'manual' ? 'task' : ORIGIN_KIND[row.kind];
}

export function rowToLogPayload(row: TimelineRow, date: string): LogEntryPayload {
  // A thread with a client is client time like a meeting is, and the client's
  // Quick Log keeps one Client Call for both rather than a task per thread.
  // Without a client there is no Quick Log to land on, and no client call.
  const internal = row.department !== 'csd';
  const clientCall =
    !internal && (row.kind === 'meeting' || row.kind === 'slack') && row.client !== null;
  return {
    // A hand-made row is nobody's proposal, so there is no suggestion for the
    // decision to point back at.
    origin: row.kind === 'manual' ? undefined : { kind: ORIGIN_KIND[row.kind], ref: row.ref },
    body: {
      date,
      durationMinutes: row.durationMinutes,
      startTs: row.startTs,
      taskId: row.kind === 'task' && !internal ? row.ref : null,
      taskName: clientCall ? 'Client Call' : row.title,
      client: internal ? null : row.client,
      clientId: internal ? null : row.clientId,
      // Internal time is filed in its department's own list.
      ...(row.department !== 'csd' ? { department: row.department } : {}),
      note: clientCall ? row.title : row.reason,
      variable: row.variable,
      taskTypeId: row.taskTypeId,
    },
  };
}

// An entry the CFO made rather than confirmed. It is drawn from the body that
// created it, so the block on the page and the record in ClickUp say the same
// thing before the two have had a chance to agree.
export function manualEntryRow(
  id: string,
  body: CreateEntryBody,
  startTs: number,
  entryId: string | null,
): TimelineRow {
  return {
    id,
    kind: 'manual',
    ref: body.taskId ?? String(startTs),
    title: body.taskName ?? 'Manual entry',
    client: body.client ?? null,
    clientId: body.clientId ?? null,
    url: null,
    startTs,
    durationMinutes: body.durationMinutes,
    reason: body.note || 'Logged',
    placement: 'clock',
    confirmed: true,
    entryId,
    variable: body.variable ?? true,
    transcript: null,
    taskTypeId: body.taskTypeId ?? null,
    taskTypeName: null,
    department: body.department ?? 'csd',
    loggedAs: null,
  };
}

export function byStartTs(a: TimelineRow, b: TimelineRow): number {
  return a.startTs - b.startTs;
}

export function applyConfirmation(
  rows: TimelineRow[],
  rowId: string,
  entryId: string | null,
  startTs: number,
): TimelineRow[] {
  return rows.map((r) =>
    r.id === rowId ? { ...r, confirmed: true, entryId, startTs, placement: 'clock' as const } : r,
  );
}

export function notLoggedMessage(failures: Array<{ title: string; reason: string }>): string {
  const count = failures.length === 1 ? '1 entry was' : `${failures.length} entries were`;
  return `${count} not logged — ${failures.map((f) => `${f.title}: ${f.reason}`).join(' · ')}`;
}

export function minutesOf(rows: TimelineRow[], confirmed: boolean): number {
  return rows.filter((r) => r.confirmed === confirmed).reduce((s, r) => s + r.durationMinutes, 0);
}

// A proposal is the model's guess, and the CFO correcting it before confirming
// is the point of the page. Corrections live here until the row is logged: an
// unconfirmed row has no entry to PATCH, and the confirm payload is built from
// the row, so an edited row logs exactly what is on screen.
export function applyRowOverrides(
  rows: TimelineRow[],
  overrides: Record<string, RowOverride>,
): TimelineRow[] {
  return rows.map((row) => (overrides[row.id] ? { ...row, ...overrides[row.id] } : row));
}

// The grid is drawn in Eastern minutes because that is the day the CFO worked;
// reading the browser's clock would slide every block by the viewer's offset.
const ET_HM = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hourCycle: 'h23',
  hour: '2-digit',
  minute: '2-digit',
});

export function etMinutesOfDay(ms: number): number {
  const [hours, minutes] = ET_HM.format(new Date(ms)).split(':').map(Number);
  return hours * 60 + minutes;
}

const ET_OFFSET = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  timeZoneName: 'longOffset',
});

function etOffsetMinutes(ms: number): number {
  const raw = ET_OFFSET.formatToParts(new Date(ms)).find((p) => p.type === 'timeZoneName')?.value;
  const match = /GMT([+-])(\d{2}):(\d{2})/.exec(raw ?? '');
  if (!match) return 0;
  return (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]));
}

// A clock reading back into the instant it names. The offset is read twice
// because the first guess can land on the wrong side of a DST change, and an
// entry an hour from where it was put is worse than no entry at all.
export function etMinuteToTs(dateIso: string, minuteOfDay: number): number {
  const [y, m, d] = dateIso.split('-').map(Number);
  const guess = Date.UTC(y, m - 1, d, 0, minuteOfDay);
  return guess - etOffsetMinutes(guess - etOffsetMinutes(guess) * 60_000) * 60_000;
}

// The working day is the floor and the day's own content is what widens it,
// with an hour of air either side. A window drawn only around the content
// shrinks to fit a single meeting, which leaves a quiet day with no empty
// ground to draw a new entry across.
export function calendarWindow(rows: Array<{ startTs: number; durationMinutes: number }>): {
  startMinute: number;
  endMinute: number;
} {
  let start = CALENDAR_DAY_START_HOUR * 60;
  let end = CALENDAR_DAY_END_HOUR * 60;

  for (const row of rows) {
    const from = etMinutesOfDay(row.startTs);
    start = Math.min(start, Math.floor((from - CALENDAR_WINDOW_PAD_MINUTES) / 60) * 60);
    end = Math.max(
      end,
      Math.ceil((from + row.durationMinutes + CALENDAR_WINDOW_PAD_MINUTES) / 60) * 60,
    );
  }

  return { startMinute: Math.max(0, start), endMinute: Math.min(24 * 60, end) };
}

// What a block has room to say. Asking for three lines in 36px is what clipped
// every half hour in the grid's first draft, and a block sharing its hour with
// two others has lost the width a second line of detail needs.
export function blockDensity(height: number, columns: number): 'full' | 'compact' | 'title' {
  if (columns >= CALENDAR_TITLE_ONLY_COLUMNS) return 'title';
  if (height >= CALENDAR_FULL_HEIGHT) return 'full';
  if (height >= CALENDAR_COMPACT_HEIGHT) return 'compact';
  return 'title';
}

// Where "now" falls in the window, and only on the day being looked at — a line
// across yesterday would be claiming something about a day already over.
export function nowMarkerMinute(
  date: string,
  windowStart: number,
  windowEnd: number,
): number | null {
  if (date !== todayIso()) return null;
  const minute = etMinutesOfDay(Date.now());
  return minute >= windowStart && minute <= windowEnd ? minute : null;
}

export function snapMinutes(minutes: number): number {
  return Math.round(minutes / CALENDAR_SNAP_MINUTES) * CALENDAR_SNAP_MINUTES;
}

// A drag is two clock readings in whichever order they were made, so the block
// it draws is the span between them — upwards draws the same block downwards
// does, and the quarter hour is the shortest thing the grid can express.
export function draftGeometry(
  selection: DraftSelection,
  windowStartMinute: number,
): { startMinute: number; durationMinutes: number; top: number; height: number } {
  const startMinute = Math.min(selection.from, selection.to);
  const durationMinutes = Math.max(CALENDAR_SNAP_MINUTES, Math.abs(selection.to - selection.from));
  return {
    startMinute,
    durationMinutes,
    top: (startMinute - windowStartMinute) * CALENDAR_PX_PER_MINUTE,
    height: durationMinutes * CALENDAR_PX_PER_MINUTE,
  };
}

// What the drawn stretch looks like to the editor before anything is logged: a
// hand-made block with no name yet, so the dialog offers it every field.
export function drawnRow(span: DrawnSpan): TimelineRow {
  return {
    ...manualEntryRow(
      `draft:${span.startTs}`,
      { date: '', ...span, variable: false },
      span.startTs,
      null,
    ),
    title: '',
    confirmed: false,
  };
}

// Where it sits comes from the gesture; everything else from the dialog, so the
// entry reaches ClickUp already filed rather than corrected afterwards.
export function drawnEntryBody(span: DrawnSpan, patch: RowOverride, date: string): CreateEntryBody {
  const department = patch.department ?? 'csd';
  return {
    date,
    startTs: span.startTs,
    durationMinutes: patch.durationMinutes ?? span.durationMinutes,
    taskName: patch.title ?? '',
    client: patch.client ?? null,
    clientId: patch.clientId ?? null,
    taskTypeId: patch.taskTypeId ?? null,
    variable: patch.variable ?? false,
    // Internal time is filed in its department's own list.
    ...(department !== 'csd' ? { department } : {}),
  };
}

// A block's top is where the clock says it began; its height is what it claims
// to have taken. They are different facts and only the second one is dragged.
export function blockGeometry(
  row: { startTs: number; durationMinutes: number },
  windowStartMinute: number,
): { top: number; height: number } {
  return {
    top: (etMinutesOfDay(row.startTs) - windowStartMinute) * CALENDAR_PX_PER_MINUTE,
    height: Math.max(CALENDAR_MIN_BLOCK_HEIGHT, row.durationMinutes * CALENDAR_PX_PER_MINUTE),
  };
}

// Overlap is read off the drawn geometry rather than the clock, because a short
// entry is drawn taller than its minutes and would otherwise cover the next.
// Blocks that reach each other through a chain share one column count, and a
// block takes the first column already free, so two that never meet can stack
// in one column instead of squeezing the whole cluster narrower.
//
// A pinned block is one being dragged: it takes the column under the pointer by
// trading whole columns with whatever held it, which keeps every column free of
// overlap without laying the cluster out again.
export function layoutBlocks(
  rows: Array<{ id: string; startTs: number; durationMinutes: number }>,
  windowStartMinute: number,
  pin?: { id: string; fraction: number },
): Map<string, BlockLayout> {
  const blocks = rows
    .map((row) => ({ id: row.id, ...blockGeometry(row, windowStartMinute) }))
    .sort((a, b) => a.top - b.top || b.height - a.height);

  const layout = new Map<string, BlockLayout>();
  let cluster: BlockLayout[] = [];
  let columnEnds: number[] = [];
  let clusterEnd = -Infinity;

  const closeCluster = () => {
    const columns = columnEnds.length;
    for (const block of cluster) block.columns = columns;

    const pinned = pin === undefined ? undefined : layout.get(pin.id);
    if (pin !== undefined && pinned !== undefined && cluster.includes(pinned)) {
      const from = pinned.column;
      const to = Math.min(columns - 1, Math.max(0, Math.floor(pin.fraction * columns)));
      for (const block of cluster) {
        if (block.column === from) block.column = to;
        else if (block.column === to) block.column = from;
      }
    }

    cluster = [];
    columnEnds = [];
  };

  for (const { id, top, height } of blocks) {
    if (top >= clusterEnd) closeCluster();
    const bottom = top + height;
    let column = columnEnds.findIndex((end) => end <= top);
    if (column === -1) column = columnEnds.length;
    columnEnds[column] = bottom;
    clusterEnd = cluster.length === 0 ? bottom : Math.max(clusterEnd, bottom);

    const block = { top, height, column, columns: 1 };
    cluster.push(block);
    layout.set(id, block);
  }
  closeCluster();

  return layout;
}

// A duration as a person writes one: "1h 30m", "1:30", "1.5" (hours) or "90"
// (minutes). Null is anything that is not a positive span within a day, which
// is what keeps Save shut rather than logging a guess at what was meant.
export function parseDuration(text: string): number | null {
  const input = text.trim().toLowerCase();
  let minutes: number | null = null;

  const clock = /^(\d+):([0-5]\d)$/.exec(input);
  const units = /^(?:(\d+)\s*h[a-z]*)?\s*(?:(\d+)\s*(?:m[a-z]*)?)?$/.exec(input);
  if (clock) {
    minutes = Number(clock[1]) * 60 + Number(clock[2]);
  } else if (/^(\d+\.\d*|\.\d+)$/.test(input)) {
    minutes = Math.round(Number(input) * 60);
  } else if (units && (units[1] !== undefined || units[2] !== undefined)) {
    minutes = Number(units[1] ?? 0) * 60 + Number(units[2] ?? 0);
  }

  return minutes !== null && minutes > 0 && minutes <= 24 * 60 ? minutes : null;
}

// One click of a duration's arrows: onto the next quarter hour in that
// direction, so an odd length joins the grid's steps instead of keeping its
// offset, and never below a quarter hour or past a day.
export function stepDuration(minutes: number, direction: 1 | -1): number {
  const next =
    direction === 1
      ? Math.floor(minutes / CALENDAR_SNAP_MINUTES + 1) * CALENDAR_SNAP_MINUTES
      : Math.ceil(minutes / CALENDAR_SNAP_MINUTES - 1) * CALENDAR_SNAP_MINUTES;
  return Math.min(24 * 60, Math.max(CALENDAR_SNAP_MINUTES, next));
}

// Whole hours only: the lines a person reads the day against. Quarter lines
// would be four times the ink for a grid the drag already snaps to.
export function calendarHours(startMinute: number, endMinute: number): number[] {
  const hours: number[] = [];
  for (let m = startMinute; m < endMinute; m += 60) hours.push(m / 60);
  return hours;
}

export function hourLabel(hour: number): string {
  const oclock = hour % 12 === 0 ? 12 : hour % 12;
  return `${oclock} ${hour < 12 || hour === 24 ? 'AM' : 'PM'}`;
}

// Two passes, because a transcript is unreadable without both. A block can
// cite more than one channel, so the lines split by conversation rather than
// interleaving; and a person sending four messages in a row is one turn, not
// four rows each restating who and when.
export function groupTranscript(lines: TranscriptLine[]): TranscriptGroup[] {
  const groups = new Map<string, TranscriptGroup>();

  for (const line of lines) {
    let group = groups.get(line.conversationId);
    if (!group) {
      group = {
        conversationId: line.conversationId,
        conversation: line.conversation,
        conversationKind: line.conversationKind,
        runs: [],
      };
      groups.set(line.conversationId, group);
    }

    // A gap long enough to be a new thought starts a new turn even from the
    // same person: an afternoon reply is not a continuation of the morning.
    const open = group.runs.at(-1);
    const sameTurn =
      open !== undefined &&
      open.speaker === line.speaker &&
      open.author === line.author &&
      line.ts - open.lastTs <= TRANSCRIPT_RUN_GAP_MS;

    if (open && sameTurn) {
      open.texts.push(line.text);
      open.lastTs = line.ts;
    } else {
      group.runs.push({
        key: `${line.conversationId}:${line.ts}`,
        speaker: line.speaker,
        author: line.author,
        ts: line.ts,
        lastTs: line.ts,
        texts: [line.text],
      });
    }
  }

  return [...groups.values()];
}

// Who said it. An id is never shown, so when Slack would not name the author
// the conversation still can: in a DM the other side is the counterparty by
// definition, which names every message on a day captured before authors were
// resolved at all.
export function transcriptAuthorLabel(run: TranscriptRun, group: TranscriptGroup): string {
  if (run.speaker === 'you') return 'You';
  if (run.author) return run.author;
  if (group.conversationKind === 'dm' && group.conversation) return group.conversation;
  return 'Them';
}

// A channel is addressed by its name and a DM by the person on the other end.
// Neither is ever an id: an id is what the CFO opened this panel to avoid.
export function transcriptConversationLabel(group: TranscriptGroup): string {
  if (group.conversationKind === 'dm') {
    return group.conversation ? `Direct message with ${group.conversation}` : 'Direct message';
  }
  return group.conversation ?? 'Slack';
}

// A row read straight off a ClickUp entry, with no suggestion behind it: the
// timeline names those `entry:<id>`.
export function isEntryOnly(row: TimelineRow): boolean {
  return row.kind === 'manual' || row.id.startsWith('entry:');
}

// Where a drag leaves a block is a choice made on the grid, so it lands on the
// grid's line even when the block started off it (a meeting at 9:04): a
// resized block ends on the quarter hour, a moved one starts on it.
export function snappedDuration(startTs: number, rawMinutes: number): number {
  const start = etMinutesOfDay(startTs);
  return Math.max(CALENDAR_SNAP_MINUTES, snapMinutes(start + rawMinutes) - start);
}

export function snappedOffset(startTs: number, rawOffsetMinutes: number): number {
  const start = etMinutesOfDay(startTs);
  return snapMinutes(start + rawOffsetMinutes) - start;
}

// A length is kept in quarter hours, never shorter than one.
export function toQuarterHours(minutes: number): number {
  return Math.max(CALENDAR_SNAP_MINUTES, snapMinutes(minutes));
}
